import React, { ReactElement, ReactNode, useState } from 'react';
import {
  Alert,
  Box as Wrapper,
  Button,
  Collapse,
  Flex,
  LightMode,
  Space,
  Stack,
  Text,
  Tooltip,
} from '@tonic-ui/react';
import { sx } from '@tonic-ui/styled-system';
import { useTranslation } from 'react-i18next';
import { useStorageState } from '../../hooks/useStorageState';
import ExternalLink from '../external-link/external-link';

const ActionButton = React.forwardRef((props, ref) => {
  return (
    <Button
      ref={ref}
      variant="secondary"
      borderColor="black:primary"
      color="black:primary"
      css={sx({
        ':active': {
          color: 'black:primary',
        },
        ':focus': {
          color: 'black:primary',
        },
        ':hover': {
          background: 'rgba(0, 0, 0, 0.12)',
          color: 'black:primary',
        },
        ':hover:not(:focus)': {
          boxShadow: 'none',
        },
      })}
      {...props}
    />
  );
});

ActionButton.displayName = 'ActionButton';

interface customErrorMessage {
  [key: string]: string;
}

type NotificationBannerProps = {
  name: string;
  severity: string;
  width?: string | number;
  showCloseButton?: boolean;
  shouldOpen: boolean;
  shouldRememberDismiss?: boolean;
  bannerText?: string | customErrorMessage;
  htmlElement?: ReactNode;
  bannerLinkTxt?: string;
  bannerLink?: string;
  isLinkInline?: boolean;
  bannerButtonTxt?: string;
  bannerButtonIcon?: ReactElement;
  backgroundColor?: string;
  clickHandler?: () => void;
  variant?: string;
};

const NotificationBanner = ({
  name = '',
  severity = 'info',
  width = '100%',
  showCloseButton = false,
  shouldOpen = false,
  shouldRememberDismiss = false,
  bannerText,
  htmlElement,
  bannerLinkTxt = '',
  bannerLink = '',
  isLinkInline = false,
  bannerButtonTxt = '',
  backgroundColor = '',
  bannerButtonIcon,
  clickHandler,
  variant = 'solid',
}: NotificationBannerProps): JSX.Element => {
  const { t } = useTranslation();
  const [uiPref, setUiPref] = useStorageState('localStorage', 'C1C_UI_PREF', {});
  const shouldOpenBasedOnDismissState = (bannerName: string): boolean => {
    if (!uiPref || !uiPref.dismissedElements || !bannerName) {
      return true;
    }
    if (uiPref?.dismissedElements?.includes(bannerName)) {
      return false;
    }
    return true;
  };

  const [isOpen, setIsOpen] = useState(() =>
    shouldRememberDismiss ? shouldOpenBasedOnDismissState(name) : shouldOpen
  );

  const handleClose = () => {
    if (shouldRememberDismiss && severity === 'info') saveDismissState();
    setIsOpen(false);
  };
  const saveDismissState = () => {
    let dismissedElements = { ...uiPref }?.dismissedElements;
    if (!dismissedElements || dismissedElements?.length === 0) {
      dismissedElements = [name];
    } else if (!dismissedElements.includes(name)) {
      dismissedElements.push(name);
    }
    setUiPref({ ...uiPref, dismissedElements: dismissedElements });
  };
  return (
    <>
      <Stack width={width} mb="1px" alignSelf="center">
        <Collapse in={isOpen}>
          <Alert
            isClosable={showCloseButton}
            onClose={handleClose}
            data-ux-id={`${name}-banner-${severity}`}
            data-id={`${name}-banner-${severity}`}
            severity={severity}
            variant={variant}
            {...(backgroundColor !== '' ? (backgroundColor = { backgroundColor }) : null)}
          >
            <Flex justify="space-between">
              <Text>
                {htmlElement
                  ? htmlElement
                  : bannerText?.errorMsgFromService
                  ? bannerText.errorMsgFromService
                  : bannerText
                  ? t(bannerText)
                  : ''}
                {bannerLink && bannerLinkTxt ? (
                  <>
                    {!isLinkInline && <Space width="0.5rem" />}
                    <LightMode display="inline-flex">
                      <Tooltip placement="bottom-end" label={t('infoBannerLinkTooltip')}>
                        <ExternalLink
                          href={bannerLink}
                          showIcon={false}
                          style={{ wordBreak: 'break-all', color: 'inherit' }}
                        >
                          {t(bannerLinkTxt)}
                        </ExternalLink>
                      </Tooltip>
                    </LightMode>
                  </>
                ) : (
                  ''
                )}
              </Text>
              {bannerButtonTxt && clickHandler && (
                <Wrapper alignSelf="center" marginLeft={15}>
                  <ActionButton size="sm" onClick={clickHandler}>
                    {bannerButtonIcon ? bannerButtonIcon : null}
                    <>{t(bannerButtonTxt)}</>
                  </ActionButton>
                </Wrapper>
              )}
            </Flex>
          </Alert>
        </Collapse>
      </Stack>
    </>
  );
};

export default NotificationBanner;
